import { useEffect, useState } from 'react';

const KEYBOARD_HEIGHT_THRESHOLD = 150;
const EDITABLE_TAG_NAMES = ['INPUT', 'TEXTAREA', 'SELECT'];

/**
 * Determines whether an editable element (input, textarea, select, or
 * contenteditable element) currently has focus.
 *
 * @returns {boolean}
 */
const isEditableElementFocused = () => {
  if (typeof document === 'undefined') return false;
  const activeElement = document.activeElement;
  if (!activeElement) return false;
  if (EDITABLE_TAG_NAMES.includes(activeElement.tagName)) return true;
  return activeElement.isContentEditable === true;
};

/**
 * Detects whether the on-screen software keyboard is currently open.
 *
 * On browsers with modern keyboard-resize behavior (visualViewport support),
 * the layout viewport stays fixed while the keyboard opens, so we rely on
 * `window.visualViewport`'s 'resize' event and compare its height against a
 * baseline captured on mount. On older engines without `visualViewport`
 * (e.g. old Android WebViews), we fall back to `window`'s 'resize' event
 * using the same height-shrink heuristic.
 *
 * A shrink alone isn't enough signal — device rotation also shrinks/grows
 * the viewport — so we additionally gate on whether an editable element
 * (input, textarea, select, or contenteditable) currently has focus.
 *
 * @returns {boolean} isKeyboardOpen
 */
const useKeyboardOpen = () => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const visualViewport = window.visualViewport;
    let baselineHeight = visualViewport ? visualViewport.height : window.innerHeight;

    const evaluate = () => {
      const currentHeight = visualViewport ? visualViewport.height : window.innerHeight;
      const hasShrunk = baselineHeight - currentHeight > KEYBOARD_HEIGHT_THRESHOLD;
      setIsKeyboardOpen(hasShrunk && isEditableElementFocused());
    };

    const handleResize = () => {
      // Only re-baseline on resize when no editable element is focused, so
      // that opening the keyboard doesn't shift the baseline out from under
      // itself, while closing it (or rotating) does correctly reset it.
      if (!isEditableElementFocused()) {
        baselineHeight = visualViewport ? visualViewport.height : window.innerHeight;
      }
      evaluate();
    };

    const handleOrientationChange = () => {
      baselineHeight = visualViewport ? visualViewport.height : window.innerHeight;
      evaluate();
    };

    const handleFocusChange = () => {
      evaluate();
    };

    if (visualViewport) {
      visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }
    window.addEventListener('orientationchange', handleOrientationChange);
    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);

    return () => {
      if (visualViewport) {
        visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, []);

  return isKeyboardOpen;
};

export default useKeyboardOpen;
